import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { TrendingUp, Clock, Users, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const ROISection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const stats = [
    {
      icon: Clock,
      value: "15+",
      label: "Hours Saved",
      sublabel: "Per Week Per Partner",
    },
    {
      icon: Users,
      value: "100%",
      label: "Team Capacity",
      sublabel: "Freed for Real Work",
    },
    {
      icon: TrendingUp,
      value: "Zero",
      label: "Lost Documents",
      sublabel: "Complete Audit Trail",
    },
    {
      icon: ShieldCheck,
      value: "24/7",
      label: "Always Working",
      sublabel: "No Supervision Needed",
    },
  ];

  const benefits = [
    "Free up your Article Assistants from data entry so they can focus on GST compliance and Verification.",
    "Eliminate the risk of lost documents and the embarrassment of asking clients for the same bill twice.",
    "Stop the 11 PM panic of wondering if all documents were received before the deadline.",
    "Create a professional image with instant, automated acknowledgments to every client.",
  ];

  const scrollToDemo = (e) => {
    e.preventDefault();
    const element = document.querySelector("#book-demo");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section
      id="roi"
      ref={ref}
      className="py-20 md:py-32 bg-navy relative overflow-hidden"
      data-testid="roi-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_30%,_white_1px,_transparent_1px)] bg-[length:80px_80px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="font-body text-sm font-semibold uppercase tracking-widest text-gold mb-4">
            The Business Case
          </p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-6"
            data-testid="roi-headline"
          >
            The ROI of Peace
          </h2>
          <p className="font-body text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            This isn't an expense. It's buying back capacity. 
            Every hour saved is an hour your team can spend on billable work.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="text-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 md:p-8"
              data-testid={`roi-stat-${index}`}
            >
              <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <stat.icon size={24} className="text-gold" />
              </div>
              <p className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                {stat.value}
              </p>
              <p className="font-body text-sm font-semibold text-white uppercase tracking-wide">
                {stat.label}
              </p>
              <p className="font-body text-xs text-white/60 mt-1">
                {stat.sublabel}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Benefits List */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-12"
        >
          <h3 className="font-heading text-2xl font-bold text-white mb-8 text-center">
            What You Actually Get
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-4"
                data-testid={`roi-benefit-${index}`}
              >
                <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <ArrowRight size={14} className="text-navy" />
                </div>
                <p className="font-body text-white/80 leading-relaxed">
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="font-body text-white/70 mb-6 text-lg">
            Ready to see what 15+ extra hours per week looks like?
          </p>
          <Button
            onClick={scrollToDemo}
            className="bg-gold text-navy font-body font-semibold tracking-wide uppercase text-sm px-8 py-4 rounded-full transition-all duration-300 hover:bg-gold/90 active:scale-98"
            data-testid="roi-cta-button"
          >
            Calculate My ROI
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ROISection;
